import { memo } from 'react';
import { DateTimePicker } from '@/components/DateTimePicker';
import { GroupedDropdownlist } from '@/components/GroupedDropdownlist';
import type { Officer, Person } from '@/components/screens/OfficersAssigned';

function formatDate(date: any): string {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatTime(date: any): string {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

interface OfficersDesktopTableProps {
  officers: Officer[];
  people: Person[];
  officerLabel: string;
  officerPluralLabel: string;
  isReadOnly: boolean;
  editingField: { uuid: string; field: string } | null;
  editValue: any;
  datePickerOpenDesktop: boolean;
  datePickerPopupClass: string;
  officerFilterPreference: boolean | null;
  hasReassignPermission: boolean;
  hasActiveCall: boolean;
  autoOpenDropdown: boolean;
  onStartEdit: (uuid: string, field: string, value: any) => void;
  onCancelEdit: () => void;
  onTogglePrimary: (uuid: string) => void;
  onDeleteConfirm: (uuid: string) => void;
  onReassignConfirm: (uuid: string) => void;
  onSetMileage: (uuid: string) => void;
  onOfficerSelect: (item: any) => void;
  onOfficerCancel: () => void;
  onDropdownTab: (e: { shiftKey: boolean }) => void;
  onDropdownClose: () => void;
  onOfficerFilterChange: (data: { isOfficerFilterOnlineOnly: boolean }) => void;
  onDateChange: (v: any) => void;
  onDatePickerOpen: () => void;
  onDatePickerClose: () => void;
  onSaveEdit: () => void;
  onClearDate: () => void;
  onTabKey: (event: any) => void;
  buildCallCardUrl: (callCardId: number | null) => string;
}

export const OfficersDesktopTable = memo(function OfficersDesktopTable({
  officers,
  people,
  officerLabel,
  officerPluralLabel,
  isReadOnly,
  editingField,
  editValue,
  datePickerOpenDesktop,
  datePickerPopupClass,
  officerFilterPreference,
  hasReassignPermission,
  hasActiveCall,
  autoOpenDropdown,
  onStartEdit,
  onCancelEdit,
  onTogglePrimary,
  onDeleteConfirm,
  onReassignConfirm,
  onSetMileage,
  onOfficerSelect,
  onOfficerCancel,
  onDropdownTab,
  onDropdownClose,
  onOfficerFilterChange,
  onDateChange,
  onDatePickerOpen,
  onDatePickerClose,
  onSaveEdit,
  onClearDate,
  onTabKey,
  buildCallCardUrl,
}: OfficersDesktopTableProps) {
  const isEditing = (rowUuid: string, field: string): boolean => {
    return editingField?.uuid === rowUuid && editingField?.field === field;
  };

  return (
    <div className="table-container">
      <table>
        <thead>
          <tr>
            <th style={{ width: '20%' }}>{officerLabel}</th>
            <th style={{ width: '8%', textAlign: 'center' }}>Primary</th>
            <th style={{ width: '16%' }}>Dispatched</th>
            <th style={{ width: '16%' }}>En-Route</th>
            <th style={{ width: '16%' }}>On Scene</th>
            <th style={{ width: '16%' }}>Cleared</th>
            <th style={{ width: '8%' }}>Actions</th>
          </tr>
        </thead>
        {officers.length === 0 ? (
          <tbody>
            <tr>
              <td colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
                No {officerPluralLabel.toLowerCase()} assigned
              </td>
            </tr>
          </tbody>
        ) : (
          <tbody>
            {officers.map((officer) => (
              <tr
                key={officer.uuid + '-' + (officer.isBusy || officer.isQueued ? 'busy' : 'available')}
                className={officer.isBusy || officer.isQueued ? 'busy-row' : undefined}
              >
                {/* Name cell */}
                <td className="name-cell">
                  {isEditing(officer.uuid, 'name') ? (
                    <div className="edit-container">
                      <GroupedDropdownlist
                        value={editValue}
                        items={people}
                        labelKey="name"
                        valueKey="id"
                        groupBy="company"
                        placeholder={`Select ${officerLabel.toLowerCase()}...`}
                        initialIsOfficerFilterOnlineOnly={officerFilterPreference ?? undefined}
                        initialOpen={autoOpenDropdown}
                        onItemChange={onOfficerSelect}
                        onCancel={onOfficerCancel}
                        onTab={onDropdownTab}
                        onClose={onDropdownClose}
                        onFilterChange={onOfficerFilterChange}
                      />
                      <button className="btn-cancel" onClick={onCancelEdit}>
                        <i className="fas fa-times" />
                      </button>
                    </div>
                  ) : (
                    <div
                      className={`officer-name${!isReadOnly ? ' editable' : ''}`}
                      onClick={() => onStartEdit(officer.uuid, 'name', officer.name)}
                    >
                      {officer.name}
                    </div>
                  )}
                </td>

                {/* Primary cell */}
                <td style={{ textAlign: 'center' }}>
                  {!officer.isBusy && !officer.isQueued ? (
                    <button
                      className={`primary-toggle${officer.isPrimary ? ' active' : ''}`}
                      onClick={() => onTogglePrimary(officer.uuid)}
                      disabled={isReadOnly}
                      title={isReadOnly ? 'Edit mode required' : 'Toggle primary'}
                    />
                  ) : (
                    <span
                      className="primary-not-available"
                      title={`Busy ${officerPluralLabel.toLowerCase()} cannot be primary`}
                    >
                      &mdash;
                    </span>
                  )}
                </td>

                {/* Busy banner or time fields */}
                {officer.isBusy || officer.isQueued ? (
                  <>
                    <td colSpan={4} className="busy-cell">
                      <div className="busy-banner">
                        <div className="busy-icon">
                          <i className="fas fa-exclamation-triangle" />
                        </div>
                        <div className="busy-content">
                          <div className="busy-status">{officer.busyReason || 'BUSY'}</div>
                          <div className="busy-event">
                            Currently on Call{' '}
                            <a
                              href={buildCallCardUrl(officer.callCardId)}
                              target="_blank"
                              rel="noopener"
                            >
                              #{officer.currentEventId}
                            </a>
                          </div>
                          {hasReassignPermission && !hasActiveCall && (
                            <div className="reassign-hint">
                              Send this call to enable reassign.
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </>
                ) : (
                  <>
                    {(['dispatched', 'enroute', 'onScene', 'cleared'] as const).map((field) => (
                      <td key={field}>
                        {isEditing(officer.uuid, field) ? (
                          <div className="edit-datetime">
                            <div className="datetime-edit-controls">
                              <DateTimePicker
                                value={editValue}
                                type="datetime"
                                format="DD MMM YYYY HH:mm:ss"
                                valueType="date"
                                showSecond={true}
                                clearable={false}
                                appendToBody={true}
                                popupClass={datePickerPopupClass}
                                open={datePickerOpenDesktop}
                                onOpen={onDatePickerOpen}
                                onChange={onDateChange}
                                onConfirm={onSaveEdit}
                                onClear={onClearDate}
                                onClose={onDatePickerClose}
                                onKeyDown={onTabKey}
                              />
                              <button
                                className="btn-clear-date"
                                onClick={onClearDate}
                                title="Clear date/time"
                              >
                                <i className="fas fa-times-circle" />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div
                            className={!isReadOnly ? 'editable' : undefined}
                            onClick={() =>
                              onStartEdit(officer.uuid, field, officer[field])
                            }
                          >
                            {officer[field] ? (
                              <>
                                <div className="datetime-display">
                                  {formatDate(officer[field])}
                                </div>
                                <div className="time-display">
                                  {formatTime(officer[field])}
                                </div>
                              </>
                            ) : (
                              <div className="empty-cell">&mdash;</div>
                            )}
                          </div>
                        )}
                      </td>
                    ))}
                  </>
                )}

                {/* Actions cell */}
                <td>
                  {hasReassignPermission && (officer.isBusy || officer.isQueued) ? (
                    <button
                      className={`btn-reassign${!hasActiveCall ? ' is-disabled' : ''}`}
                      disabled={!hasActiveCall}
                      onClick={(e) => {
                        e.preventDefault();
                        if (hasActiveCall) onReassignConfirm(officer.uuid);
                      }}
                      title={
                        hasActiveCall
                          ? 'Clear from other call and reassign here'
                          : 'Send this call to enable reassignment'
                      }
                      aria-label="Clear and Reassign"
                    >
                      <i
                        className={
                          hasActiveCall ? 'fa-solid fa-right-long' : 'fa-solid fa-lock'
                        }
                        aria-hidden="true"
                      />
                      <span>Reassign</span>
                    </button>
                  ) : (
                    <div className="actions">
                      <button
                        className="btn-icon"
                        onClick={() => onSetMileage(officer.uuid)}
                        disabled={isReadOnly}
                        title={isReadOnly ? 'Edit mode required' : 'Mileage'}
                      >
                        <img src="/Content/Images/icons8-speed-100.png" />
                      </button>
                      <button
                        className="btn-delete"
                        onClick={() => onDeleteConfirm(officer.uuid)}
                        disabled={isReadOnly}
                        title={
                          isReadOnly ? 'Edit mode required' : `Remove ${officerLabel}`
                        }
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        )}
      </table>
    </div>
  );
});
